/**
 * REST/SSE client for the panel-api agent module (plan §4).
 *
 * Auth mirrors AuthorizedApolloProvider exactly: Bearer token from
 * getAccessTokenSilently + X-Tenant-ID from localStorage. Native
 * EventSource cannot set an Authorization header, so streaming uses
 * fetch + ReadableStream parsing.
 */

import { parseSseFrames } from '../utils/sse';
import { answerForPayload } from './answer-for';
import { readSearchOwnDocs } from './search-own-docs';
import { pageContextFor } from './page-context';
import type { PageRecordType } from './page-context';

// `??` so an intentionally-empty VITE_API_ENDPOINT (staging/preview, worker
// proxies API same-origin) stays "" → relative URLs, not localhost.
const API_BASE = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

export type GetToken = () => Promise<string>;

export interface AgentSessionSummary {
  id: string;
  title: string;
  status: string;
  turnCount: number;
  createdAt: string;
  updatedAt: string;
  transcript: AgentTranscriptMessage[];
}

export interface AgentAttachmentMeta {
  fileId: string;
  filename: string;
  mimeType: string;
  size: number;
  /** Image preview: a local object URL on a message just sent, a short-lived signed URL when restored from the server. */
  previewUrl?: string;
}

export interface AgentTranscriptMessage {
  role: 'user' | 'assistant';
  text: string;
  toolCalls: string[];
  createdAt: string;
  /** Structured answer cards for this turn (present on assistant answers). */
  blocks?: AgentBlock[];
  /** Files attached to this turn (present on user turns that carried uploads). */
  attachments?: AgentAttachmentMeta[];
}

/**
 * Structured answer blocks rendered as cards in the chat. Mirrors the backend
 * `AgentBlock` union in apps/panel-api/src/agent/entities/agent-block.ts —
 * kept in lockstep by hand, like AgentStreamEvent below.
 */
export interface AgentMetricItem {
  label: string;
  value: string;
  delta?: string;
  up?: boolean;
}
export interface AgentChartPoint {
  x: string;
  value: number;
  valueLabel?: string;
  down?: boolean;
}
export interface AgentTableColumn {
  header: string;
  numeric?: boolean;
}
export interface AgentPreviewField {
  label: string;
  value: string;
}
export interface AgentActionItem {
  label: string;
  href?: string;
  variant?: 'primary' | 'secondary';
  icon?: string;
}
export interface AgentChoiceOption {
  value: string;
  label: string;
  description?: string;
  /** Multiple-choice picker: picking this one is the whole answer and sends at once. */
  exclusive?: boolean;
}
/** One plan or add-on in a plan picker. Mirrors AgentPlanOption in agent-block.ts. */
export interface AgentPlanOption {
  code: string;
  kind: 'PLAN' | 'ADD_ON';
  name: string;
  price: string | null;
  available: boolean;
  recommended: boolean;
  reason: string | null;
  includes: string[];
}

export type AgentTourStepStatus = 'PENDING' | 'VIEWED' | 'DONE' | 'SKIPPED';

/** One help article a search found. Mirrors AgentSourceItem in agent-block.ts. */
export interface AgentSourceItem {
  title: string;
  /** Public address; absent for an internal document with no link to send. */
  url?: string;
  /** "Everyone", or the role names that may read it. */
  audience: string;
  restricted: boolean;
}

export type AgentBlock =
  | { kind: 'sources'; label?: string; items: AgentSourceItem[] }
  | {
      kind: 'tour_controls';
      title: string;
      stepKey: string | null;
      position: number;
      count: number;
      paused: boolean;
      complete: boolean;
      steps: Array<{ key: string; title: string; status: AgentTourStepStatus }>;
      made: Array<{ label: string; url: string | null }>;
    }
  | { kind: 'plan_picker'; title: string; options: AgentPlanOption[]; note?: string }
  | {
      kind: 'price_card';
      title: string;
      lines: Array<{ label: string; amount: string }>;
      rates: Array<{ label: string; rate: string }>;
      total: string | null;
      note?: string;
    }
  | { kind: 'metrics'; label?: string; items: AgentMetricItem[] }
  | { kind: 'chart'; label?: string; data: AgentChartPoint[] }
  | { kind: 'table'; label?: string; columns: AgentTableColumn[]; rows: string[][] }
  | {
      kind: 'preview';
      title: string;
      tag?: string;
      icon?: string;
      fields: AgentPreviewField[];
      note?: string;
    }
  | { kind: 'callout'; tone?: 'info' | 'accent' | 'warn' | 'danger'; body: string }
  | { kind: 'actions'; items: AgentActionItem[] }
  | {
      kind: 'choice';
      choiceId: string;
      question: string;
      variant?: 'pills' | 'cards';
      /** More than one option may be ticked, then sent together with Continue. */
      multiple?: boolean;
      options: AgentChoiceOption[];
    };

/**
 * Rich display data for a catalog entity (bundle / module / variable component)
 * the agent proposes to create. Mirrors the backend CatalogArtifact in
 * apps/panel-api/src/agent/tools/catalog-artifact.ts — kept in lockstep by hand.
 */
export type CatalogArtifactEntity = 'bundle' | 'module' | 'component';
export type CatalogArtifactState = 'draft' | 'confirmed' | 'attention' | 'published';
export interface CatalogArtifactFact {
  label: string;
  value: string;
  mono?: boolean;
}
export interface CatalogArtifactRef {
  name: string;
  version?: number;
}
export interface CatalogArtifactComponent {
  name: string;
  inclusion?: string;
  price?: string;
  missing?: boolean;
}
export interface CatalogArtifactMatrixCell {
  value: string;
  sub?: string;
  missing?: boolean;
}
export interface CatalogArtifactMatrix {
  columns: string[];
  rows: Array<{ label: string; cells: CatalogArtifactMatrixCell[] }>;
}
export interface CatalogArtifact {
  entity: CatalogArtifactEntity;
  name: string;
  version?: number;
  state?: CatalogArtifactState;
  summary?: string;
  facts?: CatalogArtifactFact[];
  modules?: CatalogArtifactRef[];
  components?: CatalogArtifactComponent[];
  priceMatrix?: { label: string; matrix: CatalogArtifactMatrix };
  price?: { label: string; amount: string; sub?: string };
  /** Ordered writes one confirmation will perform (create_plan's itemised review). */
  steps?: string[];
  gaps?: string[];
}

/**
 * An optional element a create proposal supports but the user hasn't set —
 * shown on the confirmation card as an "optional additions" section. Mirrors
 * the backend OptionalAddition in
 * apps/panel-api/src/agent/tools/optional-elements.ts — kept in lockstep by hand.
 */
export interface OptionalAddition {
  label: string;
  hint: string;
}

export type AgentStreamEvent =
  | { type: 'text_delta'; text: string }
  | { type: 'tool_started'; tool: string; input: Record<string, unknown> }
  | { type: 'tool_finished'; tool: string; isError: boolean }
  | { type: 'structured_block'; block: AgentBlock }
  | {
      type: 'action_proposal';
      actionId: string;
      tool: string;
      summary: string;
      input: Record<string, unknown>;
      artifact?: CatalogArtifact;
      optionalAdditions?: OptionalAddition[];
      /** The card offers an optional "why not?" field with Decline. */
      asksDeclineReason?: boolean;
    }
  | { type: 'ui_command'; command: AgentUiCommand }
  | { type: 'awaiting_confirmation' }
  | { type: 'turn_complete'; stopReason: string }
  | { type: 'error'; message: string };

/**
 * An instruction for the page behind the chat. Mirrors UiCommand in
 * apps/panel-api/src/agent/entities/ui-command.ts; handled by
 * useAgentUiCommands (agent/ui-commands.ts).
 */
export type AgentUiCommand =
  | { kind: 'navigate'; path: string }
  | { kind: 'highlight'; recordType: PageRecordType; recordId: string }
  | { kind: 'refresh' };

export interface ActionResolution {
  actionId: string;
  status: 'CONFIRMED' | 'REJECTED' | 'EXPIRED';
  /** True once every proposal of the round is resolved — safe to continue. */
  roundComplete: boolean;
  result?: unknown;
}

async function authHeaders(getToken: GetToken): Promise<Record<string, string>> {
  const token = await getToken();
  const tenantId =
    localStorage.getItem('selectedTenantId') || sessionStorage.getItem('selectedTenantId');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
  };
}

export async function createAgentSession(
  getToken: GetToken,
  title?: string,
): Promise<AgentSessionSummary> {
  const response = await fetch(`${API_BASE}/api/v1/agent/sessions`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(getToken),
    body: JSON.stringify(title ? { title } : {}),
  });
  if (!response.ok) {
    throw new Error(`Could not start a conversation (${response.status})`);
  }
  return (await response.json()) as AgentSessionSummary;
}

export async function listAgentSessions(
  getToken: GetToken,
  options: { archived?: boolean } = {},
): Promise<AgentSessionSummary[]> {
  const query = options.archived ? '?status=archived' : '';
  const response = await fetch(`${API_BASE}/api/v1/agent/sessions${query}`, {
    credentials: 'include',
    headers: await authHeaders(getToken),
  });
  if (!response.ok) {
    throw new Error(`Could not load conversations (${response.status})`);
  }
  return (await response.json()) as AgentSessionSummary[];
}

/** Archive (hide from the history list) or restore a conversation. */
export async function setAgentSessionArchived(
  getToken: GetToken,
  sessionId: string,
  archived: boolean,
): Promise<void> {
  const action = archived ? 'archive' : 'unarchive';
  const response = await fetch(`${API_BASE}/api/v1/agent/sessions/${sessionId}/${action}`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(getToken),
  });
  if (!response.ok) {
    throw new Error(
      `Could not ${archived ? 'archive' : 'restore'} the conversation (${response.status})`,
    );
  }
}

/**
 * Permanent. The server only deletes an archived conversation. Already gone
 * counts as done, so deleting twice never reports a failure.
 */
export async function deleteAgentSession(getToken: GetToken, sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/v1/agent/sessions/${sessionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: await authHeaders(getToken),
  });
  // 404 means it is already gone (deleted in another tab, or a retried request).
  if (!response.ok && response.status !== 404) {
    throw new Error(`Could not delete the conversation (${response.status})`);
  }
}

export async function getAgentSession(
  getToken: GetToken,
  sessionId: string,
): Promise<AgentSessionSummary> {
  const response = await fetch(`${API_BASE}/api/v1/agent/sessions/${sessionId}`, {
    credentials: 'include',
    headers: await authHeaders(getToken),
  });
  if (!response.ok) {
    throw new Error(`Could not load the conversation (${response.status})`);
  }
  return (await response.json()) as AgentSessionSummary;
}

export interface PendingAction {
  actionId: string;
  tool: string;
  summary: string;
  input: Record<string, unknown>;
  artifact?: CatalogArtifact;
  optionalAdditions?: OptionalAddition[];
  asksDeclineReason?: boolean;
}

export interface McpApprovalAction {
  actionId: string;
  tool: string;
  summary: string;
  details: Record<string, unknown>;
  expiresAt: string;
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'EXPIRED';
  source: 'API key' | 'MCP OAuth connection';
  /** Persisted execution failure for a confirmed action, when present. */
  error?: string;
  /** At-most-once execution was claimed, but its durable outcome is not known yet. */
  executionOutcomeUnknown: boolean;
  /** The page offers an optional "why not?" field with Reject. */
  asksDeclineReason?: boolean;
}

/**
 * Still-open proposals for a session, so their confirmation cards can be
 * re-surfaced after a reload/navigation (the transcript restores but live
 * proposals don't). Non-critical: returns [] on failure rather than throwing.
 */
export async function getAgentPendingActions(
  getToken: GetToken,
  sessionId: string,
): Promise<PendingAction[]> {
  try {
    const response = await fetch(`${API_BASE}/api/v1/agent/sessions/${sessionId}/pending-actions`, {
      credentials: 'include',
      headers: await authHeaders(getToken),
    });
    if (!response.ok) return [];
    return (await response.json()) as PendingAction[];
  } catch {
    return [];
  }
}

/**
 * Confirm or decline a proposed action; execution happens server-side. A
 * decline may carry what the person typed in the card's "why not?" field.
 */
export async function respondAgentAction(
  getToken: GetToken,
  actionId: string,
  decision: 'confirm' | 'reject',
  reason?: string,
): Promise<ActionResolution> {
  const response = await fetch(`${API_BASE}/api/v1/agent/actions/${actionId}/${decision}`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(getToken),
    ...(decision === 'reject' && reason?.trim() && { body: JSON.stringify({ reason }) }),
  });
  if (!response.ok) {
    throw new Error(`Could not ${decision} the action (${response.status})`);
  }
  return (await response.json()) as ActionResolution;
}

/** Load one MCP write proposal through the signed-in, tenant-bound dashboard API. */
export async function getMcpApprovalAction(
  getToken: GetToken,
  actionId: string,
): Promise<McpApprovalAction> {
  const response = await fetch(`${API_BASE}/api/v1/agent/mcp-actions/${actionId}`, {
    credentials: 'include',
    headers: await authHeaders(getToken),
  });
  if (!response.ok) {
    throw new Error(`Could not load the MCP approval (${response.status})`);
  }
  return (await response.json()) as McpApprovalAction;
}

/** Approve or reject an MCP proposal out of band; MCP itself exposes no approval tool. */
export async function respondMcpApprovalAction(
  getToken: GetToken,
  actionId: string,
  decision: 'approve' | 'reject',
  reason?: string,
): Promise<Omit<ActionResolution, 'roundComplete'>> {
  const response = await fetch(`${API_BASE}/api/v1/agent/mcp-actions/${actionId}/${decision}`, {
    method: 'POST',
    credentials: 'include',
    headers: await authHeaders(getToken),
    ...(decision === 'reject' && reason?.trim() && { body: JSON.stringify({ reason }) }),
  });
  if (!response.ok) {
    throw new Error(`Could not ${decision} the MCP action (${response.status})`);
  }
  return (await response.json()) as Omit<ActionResolution, 'roundComplete'>;
}

/**
 * Send one message and consume the SSE response. Resolves with the full
 * assistant text once the turn completes; onEvent fires for every frame
 * (text deltas, tool activity) so callers can render progress live.
 */
export async function streamAgentMessage(options: {
  getToken: GetToken;
  sessionId: string;
  /** Omit to CONTINUE a conversation after a confirmed/declined action. */
  message?: string;
  /** StoredFile ids to attach to this message for the agent to read. */
  attachments?: string[];
  onEvent?: (event: AgentStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<{ reply: string }> {
  const { getToken, sessionId, message, attachments, onEvent, signal } = options;
  const isContinue = message === undefined;
  // The page the message is sent from; the assistant hears about the record on
  // screen only after the server re-reads it inside the tenant.
  const pageContext =
    typeof window === 'undefined' ? undefined : pageContextFor(window.location.pathname);
  // Who the reply is for, when the person picked someone beside the composer. A continue sends it
  // too, so a search after a confirmed action still reads as that customer.
  const answerFor = answerForPayload();
  // "Your docs" switched on beside the composer; a continue keeps it, like the answer-for choice.
  const searchOwnDocs = readSearchOwnDocs();
  const url = isContinue
    ? `${API_BASE}/api/v1/agent/sessions/${sessionId}/continue`
    : `${API_BASE}/api/v1/agent/sessions/${sessionId}/messages`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { ...(await authHeaders(getToken)), Accept: 'text/event-stream' },
    ...(isContinue
      ? answerFor || searchOwnDocs
        ? {
            body: JSON.stringify({
              ...(answerFor && { answerFor }),
              ...(searchOwnDocs && { searchOwnDocs }),
            }),
          }
        : {}
      : {
          body: JSON.stringify({
            message,
            ...(attachments && attachments.length > 0 && { attachments }),
            ...(pageContext && { pageContext }),
            ...(answerFor && { answerFor }),
            ...(searchOwnDocs && { searchOwnDocs }),
          }),
        }),
    signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`The assistant is unavailable right now (${response.status})`);
  }

  let reply = '';
  let streamError: string | null = null;

  for await (const event of parseSseStream(response.body)) {
    if (event.type === 'text_delta') {
      reply += event.text;
    }
    if (event.type === 'error') {
      streamError = event.message;
    }
    onEvent?.(event);
  }

  if (streamError && !reply) {
    throw new Error(streamError);
  }
  return { reply };
}

/**
 * Parse `event: <type>\ndata: <json>\n\n` frames from the agent endpoint.
 *
 * Thin typed alias over the shared parser, kept as a named export because the
 * agent's stream tests drive it directly.
 */
export function parseSseStream(body: ReadableStream<Uint8Array>): AsyncGenerator<AgentStreamEvent> {
  return parseSseFrames<AgentStreamEvent>(body);
}
